"use client";

import { useState, useEffect } from "react";
import { Dialog, DialogPanel, PopoverGroup } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation("header"); // Load the "header.json" namespace

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Handle mobile link click
  const handleLinkClick = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  return (
    <header
      className={`sticky top-0 z-50 bg-black shadow-md ${
        scrolled ? "backdrop-blur-sm" : ""
      }`}
    >
      <nav
  aria-label="Global"
  className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8 relative"
>
        <div className="flex lg:flex-1">
          <a href="/" className="-m-1.5 p-1.5">
            <span className="sr-only">{t("logoAltText")}</span>
            <img
              alt={t("logoAltText")}
              src="/assets/images/logos/Without_BG_BLACK.png"
              className="h-14 w-auto"
            />
          </a>
        </div>
        <div className="lg:hidden absolute right-4 top-6">
  <button
    type="button"
    onClick={() => setMobileMenuOpen(true)}
    className="inline-flex items-center justify-center rounded-md p-3 text-white focus:outline-none"
  >
    <span className="sr-only">{t("openMenu")}</span>
    <Bars3Icon aria-hidden="true" className="h-8 w-8" />
  </button>
</div>


        <PopoverGroup className="hidden lg:flex lg:gap-x-12">
          {[
            { key: "home", path: "/" },
            { key: "customization", path: "/customization" },
            { key: "whatWeDo", path: "/whatwedo" },
            { key: "contactUs", path: "/contactus" },
            { key: "aboutUs", path: "/aboutus" },
          ].map(({ key, path }, index) => (
            <a
              key={index}
              href={path}
              className="relative text-sm font-semibold leading-6 text-white group"
            >
              {t(key)}
              <span
                className="absolute bottom-0 left-0 h-0.5 w-0  transition-all duration-300 group-hover:w-full"
                style={{ backgroundColor: "#cfa666" }}
              ></span>
            </a>
          ))}
        </PopoverGroup>
        <div className="hidden lg:flex lg:flex-1 lg:justify-end">
          <Link
            to="/login"
            className="relative text-sm font-semibold leading-6 text-white group"
          >
            {t("login")} <span aria-hidden="true">&rarr;</span>
            <span
              className="absolute bottom-0 left-0 h-0.5 w-0 bg-white transition-all duration-300 group-hover:w-full"
            ></span>
          </Link>
        </div>
      </nav>

      {/* Mobile Menu Dialog */}
      <Dialog
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
        className="lg:hidden"
      >
        {/* Background Overlay */}
        <div className="fixed inset-0 z-10 bg-black/50 backdrop-blur-sm" />
        <DialogPanel className="fixed inset-y-0 right-0 z-20 w-full overflow-y-auto bg-black px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center justify-between">
            <a href="/" className="-m-1.5 p-1.5">
              <span className="sr-only">{t("logoAltText")}</span>
              <img
                alt={t("logoAltText")}
                src="/assets/images/logos/Without_BG_BLACK.png"
                className="h-14 w-auto"
              />
            </a>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="-m-2.5 rounded-md p-2.5 text-white"
            >
              <span className="sr-only">{t("closeMenu")}</span>
              <XMarkIcon aria-hidden="true" className="h-6 w-6" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="space-y-2 py-6">
                {[
                  { key: "home", path: "/" },
                  { key: "customization", path: "/customization" },
                  { key: "whatWeDo", path: "/whatwedo" },
                  { key: "contactUs", path: "/contactus" },
                  { key: "aboutUs", path: "/aboutus" },
                ].map(({ key, path }, index) => (
                  <button
                    key={index}
                    onClick={() => handleLinkClick(path)}
                    className="-mx-3 block w-full rounded-lg px-3 py-2 text-left text-base font-semibold leading-7 text-white hover:bg-gray-700"
                  >
                    {t(key)}
                  </button>
                ))}
              </div>
              <div className="py-6">
                <button
                  onClick={() => handleLinkClick("/login")}
                  className="-mx-3 block w-full rounded-lg px-3 py-2.5 text-left text-base font-semibold leading-7 text-white hover:bg-gray-700"
                >
                  {t("login")}
                </button>
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  );
};

export default Header;
